// Cookie Consent Management Utility

const COOKIE_CONSENT_KEY = 'cookieConsent';
const COOKIE_CONSENT_DATE_KEY = 'cookieConsentDate';


export const getCookieConsent = () => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch (error) {
    console.warn('Could not access localStorage:', error);
    return null;
  }
};

export const setCookieConsent = (status) => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, status);
    localStorage.setItem(COOKIE_CONSENT_DATE_KEY, new Date().toISOString());
    return true;
  } catch (error) {
    console.warn('Could not save cookie consent:', error);
    return false;
  }
};

export const getCookieConsentDate = () => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_DATE_KEY);
  } catch (error) {
    console.warn('Could not access localStorage:', error);
    return null;
  }
};

export const hasValidCookieConsent = () => {
  const consent = getCookieConsent();
  const consentDate = getCookieConsentDate();

  if (!consent || !consentDate) {
    return false;
  }

  // Check if consent is older than 1 year (GDPR requirement)
  const consentTime = new Date(consentDate).getTime();
  const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);

  return consentTime > oneYearAgo;
};

export const shouldShowCookieConsent = () => {
  return !hasValidCookieConsent();
};

// Cookie categories for granular control
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  PREFERENCES: 'preferences'
};

export const getCookiePreferences = () => {
  try {
    const preferences = localStorage.getItem('cookiePreferences');
    return preferences ? JSON.parse(preferences) : {
      [COOKIE_CATEGORIES.NECESSARY]: true, // Always true
      [COOKIE_CATEGORIES.ANALYTICS]: false,
      [COOKIE_CATEGORIES.MARKETING]: false,
      [COOKIE_CATEGORIES.PREFERENCES]: false
    };
  } catch (error) {
    console.warn('Could not access cookie preferences:', error);
    return {
      [COOKIE_CATEGORIES.NECESSARY]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: false,
      [COOKIE_CATEGORIES.MARKETING]: false,
      [COOKIE_CATEGORIES.PREFERENCES]: false
    };
  }
};

export const setCookiePreferences = (preferences) => {
  try {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.warn('Could not save cookie preferences:', error);
    return false;
  }
};

// Helper to check if specific cookie category is allowed
export const isCookieCategoryAllowed = (category) => {
  const consent = getCookieConsent();
  const preferences = getCookiePreferences();

  if (consent === 'rejected') {
    return category === COOKIE_CATEGORIES.NECESSARY;
  }

  if (consent === 'accepted') {
    return true;
  }

  // For custom preferences
  return preferences[category] || false;
};

// Analytics helper
export const trackCookieConsent = (action, details = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cookie_consent', {
      event_category: 'privacy',
      event_label: action,
      value: 1,
      ...details
    });
  }
};

// ===== TOKEN MANAGEMENT FUNCTIONS =====

// Helper function to get authentication token with fallback strategy
export const getAuthToken = () => {
  // Try localStorage first (for users who accept cookies)
  try {
    const localToken = localStorage.getItem('token');
    if (localToken) return localToken;
  } catch (error) {
    console.warn('localStorage not available:', error);
  }

  // Try sessionStorage as fallback (for users who reject cookies)
  try {
    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) return sessionToken;
  } catch (error) {
    console.warn('sessionStorage not available:', error);
  }

  // Try URL parameters as last resort (for users who reject all storage)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) return urlToken;
  }

  return null;
};

// Helper function to store token with fallback strategy
export const storeAuthToken = (token) => {
  if (!token) return;

  // Try localStorage first
  try {
    localStorage.setItem('token', token);
    return;
  } catch (error) {
    console.warn('localStorage not available, trying sessionStorage:', error);
  }

  // Fallback to sessionStorage
  try {
    sessionStorage.setItem('token', token);
  } catch (error) {
    console.warn('sessionStorage not available:', error);
  }
};

// Helper function to clear token from all storage locations
export const clearAuthToken = () => {
  try {
    localStorage.removeItem('token');
  } catch (error) {
    console.warn('Could not clear localStorage token:', error);
  }

  try {
    sessionStorage.removeItem('token');
  } catch (error) {
    console.warn('Could not clear sessionStorage token:', error);
  }

  // Clear from URL if present
  if (typeof window !== 'undefined' && window.history.replaceState) {
    const url = new URL(window.location);
    url.searchParams.delete('token');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
};

// Helper function to migrate token based on cookie consent
export const migrateTokenBasedOnConsent = () => {
  const consent = getCookieConsent();

  if (consent === 'rejected') {
    // Move token from localStorage to sessionStorage for users who rejected cookies
    try {
      const localToken = localStorage.getItem('token');
      if (localToken) {
        sessionStorage.setItem('token', localToken);
        localStorage.removeItem('token');
        console.log('Token migrated to sessionStorage due to cookie rejection');
      }
    } catch (error) {
      console.warn('Could not migrate token:', error);
    }
  }
};

// Helper function to check if user is authenticated with fallback strategy
export const isUserAuthenticated = () => {
  return getAuthToken() !== null;
};

// Apply existing consent on page load (should be called early in app initialization)
export const applyExistingConsent = () => {
  const consent = getCookieConsent();
  const preferences = getCookiePreferences();

  if (!consent) {
    return; // No consent given yet
  }

  // Apply tracking tool settings based on existing consent
  if (preferences[COOKIE_CATEGORIES.ANALYTICS]) {
    // Enable analytics tools
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'consent_update',
        'analytics_storage': 'granted'
      });
    }
  }

  if (preferences[COOKIE_CATEGORIES.MARKETING]) {
    // Enable marketing tools
    if (window.fbq) {
      window.fbq('consent', 'grant');
      window.fbq('track', 'PageView');
    }
    if (window.ttq) {
      window.ttq.enableCookie();
      window.ttq.page();
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'consent_update',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  }
};

// Helper function to check if tracking tools are available
export const checkTrackingToolsAvailability = () => {
  const tools = {
    gtag: !!window.gtag,
    dataLayer: !!window.dataLayer,
    fbq: !!window.fbq,
    ttq: !!window.ttq,
    _lt: !!window._lt
  };

  return tools;
};